import * as React from 'react';
import { AxiosResponse } from 'axios';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import Select, { components, ControlProps, Props, StylesConfig } from 'react-select';
import moment from 'moment';

import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useLoadingStore from 'zustands/useLoadingStore';

import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info_icon.svg';
import MonthInput from 'common/month-input/month-input';
import HolidayAllowanceModal from './holiday-allowance-modal/holiday-allowance-modal';
import payrollRegisterData from 'dummy-data/payroll-register.json';
import PayrollGrid from 'components/payroll-register/payrollGrid/PayrollGrid';
import './payroll-register.scss';
import { IOption } from 'types/common';
import useUserStore from 'zustands/useUserStore';
import AgGrid from 'components/AgGrid';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { addComma, formatTimeNumberToKorean, formatTimeValueToText } from 'constants/utils';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import useStoreStore from 'zustands/useStoreStore';

export interface IPay {
  NUMBER?: number;
  NAME?: string; // 성명
  // BIRTHDATE?: string; // 주민등록번호
  INSURANCE?: string; // 공제유형
  PAY?: number; // 기본급
  MEALS?: number; // 식대  (월급직)
  SELF_DRIVING?: number; // 자가운전  (월급직)
  BONUS?: number; // 상여  (월급직)
  INCENTIVE?: number; // 성과급  (월급직)
  FORECASTPAY?: number; // 예상급여 (시급직)
  WORKDAY?: number; // 근무일수 (시급직)
  WORKTIME?: string | number; // 근무시간 (시급직)
  WEEKPAYCOUNT?: string; // 주휴수당 횟수
  WEEKPAYINFO?: IWeekPayInfo[]; // 주휴수당 내역
  WEEKPAY?: number; // 주휴수당 (시급직)
  PLUSPAY?: number; // 가산급여 (시급직)
  RESTPAY?: number; // 휴일급여 (시급직)
  MINUSPAY?: number; // 차감급여 (시급직)
  TOTALPAY?: number; // 임금액
  ALLPAY?: number; // 총급여액
  MINUSWORKTIME?: string | number;
  REALWORKTIME?: string | number;
  NIGHTPLUSPAY?: number;
  PENSION?: number; // 국민연금  (공제)
  HEALTH?: number; // 건강보험  (공제)
  LONGCARE?: number; // 장기요양  (공제)
  EMPLOYMENT?: number; // 고용보험  (공제)
  INCOME?: number; // 소득세  (공제)
  LOCALINCOME?: number; // 소득세  (공제)
  DEDUCTION?: number; // 공제합계  (공제)
  REALPAY?: number; // 실지급액
  START_END?: string; // 입사일/퇴사일
}
export interface IWeekPayInfo {
  START: string;
  END: string;
  PAY: string;
}

const PayrollRegister: React.FunctionComponent = () => {
  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const { userType, managerInfo } = useUserStore();
  const { storeName } = useStoreStore();

  const { checkErrorLog } = useErrorLog();
  const { webApiNode } = useApi();

  const [currentMonth, setCurrentMonth] = React.useState<Date>(moment().toDate()); // 선택한 월
  const [payType, setPayType] = React.useState<IOption>({
    // 선택한 급여유형
    value: 'hour',
    label: '시급',
  });
  const [calDay, setCalDay] = React.useState<number>(0); // 급여정산일
  const [calPeriodStart, setCalPeriodStart] = React.useState<Date>(new Date()); // 해당월 급여 시작일
  const [calPeriodEnd, setCalPeriodEnd] = React.useState<Date>(new Date()); // 해당월 급여 종료일

  const [columns, setColumns] = React.useState<(ColDef | ColGroupDef)[]>([]);
  const [payData, setPayData] = React.useState<IPay[]>([]);
  const [payTotalData, setPayTotalData] = React.useState<IPay[]>([]);

  const [holidayAllowanceModal, setHolidayAllowanceModal] = React.useState<boolean>(false);
  const [weekPayInfoModalDatas, setWeekPayInfoModalDatas] = React.useState<IWeekPayInfo[]>([]); // 주휴수당 내역 모달 데이터

  const fetchData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getEmpsPay`, {
        YEAR: moment(currentMonth).year(),
        MONTH: moment(currentMonth).month() + 1,
        TYPE: payType?.value === 'hour' ? '0' : payType?.value === 'month' ? '2' : '1',
      });
      console.log('/auth/getEmpsPay_data', data);
      if (data.resultCode === 0) {
        setCalDay(Number(data.resultData.storeinfo.STORE_DAY));
        setCalPeriodStart(moment(data.resultData.storeinfo.START).toDate());
        setCalPeriodEnd(moment(data.resultData.storeinfo.END).toDate());
        if (userType === '1' || managerInfo.storePayShow) {
          formatPayData(data.resultData.empinfo);
        } else if (managerInfo.payShow) {
          formatPayData(data.resultData.empinfo.filter((i: any) => Number(i.EMP_SEQ) === managerInfo.empSeq));
        } else {
          setModalState({
            isModalShow: true,
            title: '급여 보기 권한 없음',
            desc: '사업주님이 권한을 부여하실 수 있습니다',
          });
        }
      } else {
        throw new Error('/auth/getEmpsPay');
      }
    } catch (err) {
      checkErrorLog(err, 'payroll-register /auth/getemppay');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const formatPayData = (data: IPay[]) => {
    let totalFormat: any[] = [
      {
        NUMBER: '합계',
      },
    ];
    const form = data.map((item: any, _i) => {
      let ALLPAY = 0;
      let WORKDAY = 0;
      let WORKTIME = 0;
      let TOTALPAY = 0;
      let INSURANCE = '4대보험';
      let INCOME = 0;
      let LOCALINCOME = 0;

      if (item.PAY_TYPE == '2') {
        // 월급직
        ALLPAY = Math.ceil(item.realtotal);
      } else if (item.PAY_TYPE == '1') {
        // 일급직
        WORKDAY = item.count;
        TOTALPAY = Math.ceil(item.realtotal);
      } else {
        // 시급직
        WORKDAY = item.WORKDAY;
        TOTALPAY = Math.ceil(item.bascitotal);
        ALLPAY = Math.ceil(item.TOTALPAY);
      }

      if (item.insurance == '2') {
        // 4대보험 적용
        INSURANCE = '4대보험';
        INCOME = item.earned; // 소득세
        LOCALINCOME = Math.ceil(item.earned2 / 10) * 10; // 지방소득세
      } else if (item.insurance == '1') {
        // 프리랜서
        INSURANCE = '프리랜서';
        INCOME = item.earned; // 소득세
        LOCALINCOME = Math.ceil(item.earned2 / 10) * 10; // 지방소득세
      } else {
        // 4대 보험 미적용
        INSURANCE = '없음';
        INCOME = 0;
        LOCALINCOME = 0;
      }

      if (item.WORKDAY == null) {
        WORKDAY = 0; // 근무일수
        WORKTIME = 0; // 근무시간
      } else {
        WORKDAY = item.WORKDAY;
        WORKTIME = item.WORKTIME;
      }

      if (payType.label === '월급') {
        totalFormat[0].PAY = totalFormat[0].PAY ? totalFormat[0].PAY + Math.ceil(item.PAY) : Math.ceil(item.PAY);
        totalFormat[0].MEALS = totalFormat[0].MEALS
          ? totalFormat[0].MEALS + Math.ceil(item.MEALS)
          : Math.ceil(item.MEALS);
        totalFormat[0].SELF_DRIVING = totalFormat[0].SELF_DRIVING
          ? totalFormat[0].SELF_DRIVING + Math.ceil(item.SELF_DRIVING)
          : Math.ceil(item.SELF_DRIVING);
        totalFormat[0].BONUS = totalFormat[0].BONUS
          ? totalFormat[0].BONUS + Math.ceil(item.BONUS)
          : Math.ceil(item.BONUS);
        totalFormat[0].INCENTIVE = totalFormat[0].INCENTIVE
          ? totalFormat[0].INCENTIVE + Math.ceil(item.INCENTIVE)
          : Math.ceil(item.INCENTIVE);
        totalFormat[0].ALLPAY = totalFormat[0].ALLPAY ? totalFormat[0].ALLPAY + ALLPAY : ALLPAY;
      } else if (payType.label === '일급') {
        totalFormat[0].TOTALPAY = totalFormat[0].TOTALPAY ? totalFormat[0].TOTALPAY + TOTALPAY : TOTALPAY;
      } else {
        totalFormat[0].TOTALPAY = totalFormat[0].TOTALPAY ? totalFormat[0].TOTALPAY + TOTALPAY : TOTALPAY;
        totalFormat[0].WEEKPAY = totalFormat[0].WEEKPAY
          ? totalFormat[0].WEEKPAY + Math.ceil(item.weekpaytotal)
          : Math.ceil(item.weekpaytotal);
        totalFormat[0].NIGHTPLUSPAY = totalFormat[0].NIGHTPLUSPAY
          ? totalFormat[0].NIGHTPLUSPAY + Math.ceil(item.night)
          : Math.ceil(item.night);
        totalFormat[0].PLUSPAY = totalFormat[0].PLUSPAY
          ? totalFormat[0].PLUSPAY + Math.ceil(item.plus)
          : Math.ceil(item.plus);
        totalFormat[0].RESTPAY = totalFormat[0].RESTPAY
          ? totalFormat[0].RESTPAY + Math.ceil(item.restworkpay)
          : Math.ceil(item.restworkpay);
        totalFormat[0].MINUSPAY = totalFormat[0].MINUSPAY
          ? totalFormat[0].MINUSPAY + Math.ceil(item.minertotalpay)
          : Math.ceil(item.minertotalpay);
        totalFormat[0].ALLPAY = totalFormat[0].ALLPAY ? totalFormat[0].ALLPAY + ALLPAY : ALLPAY;
      }
      totalFormat[0].PENSION = totalFormat[0].PENSION
        ? totalFormat[0].PENSION + Math.ceil(item.pension_pay)
        : Math.ceil(item.pension_pay);
      totalFormat[0].HEALTH = totalFormat[0].HEALTH
        ? totalFormat[0].HEALTH + Math.ceil(item.health_pay)
        : Math.ceil(item.health_pay);
      totalFormat[0].LONGCARE = totalFormat[0].LONGCARE
        ? totalFormat[0].LONGCARE + Math.ceil(item.health2_pay)
        : Math.ceil(item.health2_pay);
      totalFormat[0].EMPLOYMENT = totalFormat[0].EMPLOYMENT
        ? totalFormat[0].EMPLOYMENT + Math.ceil(item.employment_pay)
        : Math.ceil(item.employment_pay);
      totalFormat[0].INCOME = totalFormat[0].INCOME ? totalFormat[0].INCOME + INCOME : INCOME;
      totalFormat[0].LOCALINCOME = totalFormat[0].LOCALINCOME ? totalFormat[0].LOCALINCOME + LOCALINCOME : LOCALINCOME;
      totalFormat[0].DEDUCTION = totalFormat[0].DEDUCTION
        ? totalFormat[0].DEDUCTION +
          Math.ceil(item.pension_pay + item.health_pay + item.health2_pay + item.employment_pay + LOCALINCOME + INCOME)
        : Math.ceil(item.pension_pay + item.health_pay + item.health2_pay + item.employment_pay + LOCALINCOME + INCOME);
      totalFormat[0].REALPAY = totalFormat[0].REALPAY
        ? totalFormat[0].REALPAY + Math.ceil(item.REALTOTALPAY)
        : Math.ceil(item.REALTOTALPAY ?? 0);

      return {
        NUMBER: _i + 1,
        NAME: decodeURI(item.EMP_NAME), // 성명
        // BIRTHDATE: item.BIRTHDATE ? moment(item.BIRTHDATE).format('YYYY.MM.DD') : '0000.00.00', // 주민등록번호
        PAY: Math.ceil(item.PAY), // 기본급

        WORKDAY: WORKDAY, // 근무일수 (시급직)
        TOTALPAY: TOTALPAY, // 임금액
        ALLPAY: ALLPAY, // 총급여액
        REALPAY: Math.ceil(item.REALTOTALPAY), // 실지급액

        INSURANCE: INSURANCE, // 공제유형
        PENSION: Math.ceil(item.pension_pay), // 국민연금  (공제)
        HEALTH: Math.ceil(item.health_pay), // 건강보험  (공제)
        LONGCARE: Math.ceil(item.health2_pay), // 장기요양  (공제)
        EMPLOYMENT: Math.ceil(item.employment_pay), // 고용보험  (공제)

        INCOME: INCOME, // 소득세  (공제)
        LOCALINCOME: LOCALINCOME, // 소득세  (공제)
        DEDUCTION: Math.ceil(
          item.pension_pay + item.health_pay + item.health2_pay + item.employment_pay + LOCALINCOME + INCOME,
        ), // 공제합계  (공제)
        START_END: `${item.WORK_START}/${item.WORK_END}`, // 입사일/퇴사일

        MEALS: Math.ceil(item.MEALS), // 식대  (월급직)
        SELF_DRIVING: Math.ceil(item.SELF_DRIVING), // 자가운전  (월급직)
        BONUS: Math.ceil(item.BONUS), // 상여  (월급직)
        INCENTIVE: Math.ceil(item.INCENTIVE), // 성과급  (월급직)

        FORECASTPAY: item.FORECASTPAY, // 예상급여 (시급직)
        WORKTIME: formatTimeValueToText(item.WORKTIME), // 근무시간 (시급직)
        WEEKPAYCOUNT: item.weekcheckcount + '/' + item.weekallcount, // 주휴수당 횟수
        WEEKPAYINFO: item.weekpay, // 주휴수당 내역
        WEEKPAY: Math.ceil(item.weekpaytotal), // 주휴수당 (시급직)
        NIGHTPLUSPAY: Math.ceil(item.night),
        PLUSPAY: Math.ceil(item.plus), // 가산급여 (시급직)
        RESTPAY: Math.ceil(item.restworkpay), // 휴일급여 (시급직)
        MINUSPAY: Math.ceil(item.minertotalpay), // 차감급여 (시급직)

        MINUSWORKTIME: formatTimeValueToText(item.miner_time),
        REALWORKTIME: formatTimeValueToText(WORKTIME - item.miner_time),
      };
    });
    setPayData(form);
    setPayTotalData([
      {
        ...totalFormat[0],
        PAY: totalFormat[0]?.PAY,
        MEALS: totalFormat[0]?.MEALS,
        SELF_DRIVING: totalFormat[0]?.SELF_DRIVING,
        BONUS: totalFormat[0]?.BONUS,
        INCENTIVE: totalFormat[0]?.INCENTIVE,
        ALLPAY: totalFormat[0]?.ALLPAY,
        TOTALPAY: totalFormat[0]?.TOTALPAY,
        WEEKPAY: totalFormat[0]?.WEEKPAY,
        NIGHTPLUSPAY: totalFormat[0]?.NIGHTPLUSPAY,
        PLUSPAY: totalFormat[0]?.PLUSPAY,
        RESTPAY: totalFormat[0]?.RESTPAY,
        MINUSPAY: totalFormat[0]?.MINUSPAY,
        PENSION: totalFormat[0]?.PENSION,
        HEALTH: totalFormat[0]?.HEALTH,
        LONGCARE: totalFormat[0]?.LONGCARE,
        EMPLOYMENT: totalFormat[0]?.EMPLOYMENT,
        INCOME: totalFormat[0]?.INCOME,
        LOCALINCOME: totalFormat[0]?.LOCALINCOME,
        DEDUCTION: totalFormat[0]?.DEDUCTION,
        REALPAY: totalFormat[0]?.REALPAY,
      },
    ]);
  };

  const handleDateChange = (date: any) => {
    setCurrentMonth(date);
  };

  const colourStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '40px',
      fontSize: '14px',
      fontWeight: '400',
      color: '#868686',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  const options = [
    { value: 'hour', label: '시급' },
    { value: 'month', label: '월급' },
    { value: 'day', label: '일급' },
  ];

  const handlePayrollInformation = (selectedOption: any) => {
    setPayType(selectedOption);
  };

  const toggleModal = React.useCallback(
    (data: IWeekPayInfo[]) => {
      setWeekPayInfoModalDatas(data);
      setHolidayAllowanceModal(!holidayAllowanceModal);
    },
    [holidayAllowanceModal],
  );

  // 그리드 컴포넌트의 엑셀 다운기능
  const excelDown = () => {
    if (payData.length == 0) {
      setModalState({
        isModalShow: true,
        title: 'Excel 다운',
        desc: '급여 데이터가 없습니다.',
      });
    } else {
      // if (gridComponentRef?.current) {
      //   gridComponentRef.current.save();
      // }
      if (AgGridRef.current) {
        AgGridRef.current.api.exportDataAsExcel({
          fileName: `${storeName}_${payType.label}직급여(${moment(currentMonth).format('YYYY년MM월')})`,
          sheetName: '샵솔',
          headerRowHeight: 30,
          autoConvertFormulas: true,
        });
      }
    }
  };

  const handleColumns = (payType: IOption) => {
    if (payType.label === '월급') {
      setColumns([
        {
          field: 'NUMBER',
          headerName: '번호',
          pinned: 'left',
          width: 100,
          valueFormatter: ({ value }) => value,
        },
        {
          field: 'NAME',
          headerName: '성명',
          pinned: 'left',
          width: 100,
        },
        {
          field: 'INSURANCE',
          headerName: '공제유형',
          width: 100,
        },
        {
          headerName: '급여내역',
          children: [
            {
              field: 'PAY',
              headerName: '기본급',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'MEALS',
              headerName: '식대',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'SELF_DRIVING',
              headerName: '자가운전',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'BONUS',
              headerName: '상여',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'INCENTIVE',
              headerName: '성과급',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'ALLPAY',
              headerName: '급여총액',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
          ],
        },
        {
          headerName: '공제내역',
          children: [
            {
              field: 'PENSION',
              headerName: '국민연금',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'HEALTH',
              headerName: '건강보험',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'LONGCARE',
              headerName: '장기요양',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'EMPLOYMENT',
              headerName: '고용보험',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'INCOME',
              headerName: '소득세',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'LOCALINCOME',
              headerName: '지방소득세',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'DEDUCTION',
              headerName: '공제합계',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
          ],
        },
        {
          field: 'REALPAY',
          headerName: '실지급액',
          valueFormatter: ({ value }) => addComma(value),
          cellStyle: {
            textAlign: 'right',
          },
        },
        {
          headerName: '비고',
          children: [
            {
              field: 'START_END',
              headerName: '입사일/퇴사일',
            },
          ],
        },
      ]);
    } else if (payType.label === '일급') {
      setColumns([
        {
          field: 'NUMBER',
          headerName: '번호',
          pinned: 'left',
          width: 100,
          valueFormatter: ({ value }) => value,
        },
        {
          field: 'NAME',
          headerName: '성명',
          pinned: 'left',
          width: 100,
        },
        {
          field: 'INSURANCE',
          headerName: '공제유형',
          width: 100,
        },
        {
          headerName: '급여내역',
          children: [
            {
              field: 'PAY',
              headerName: '일급',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'WORKDAY',
              headerName: '근무일수',
            },
            {
              field: 'TOTALPAY',
              headerName: '임금액',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
          ],
        },
        {
          headerName: '공제내역',
          children: [
            {
              field: 'PENSION',
              headerName: '국민연금',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'HEALTH',
              headerName: '건강보험',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'LONGCARE',
              headerName: '장기요양',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'EMPLOYMENT',
              headerName: '고용보험',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'INCOME',
              headerName: '소득세',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'LOCALINCOME',
              headerName: '지방소득세',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'DEDUCTION',
              headerName: '공제합계',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
          ],
        },
        {
          field: 'REALPAY',
          headerName: '실지급액',
          valueFormatter: ({ value }) => addComma(value),
          cellStyle: {
            textAlign: 'right',
          },
        },
        {
          headerName: '비고',
          children: [
            {
              field: 'START_END',
              headerName: '입사일/퇴사일',
            },
          ],
        },
      ]);
    } else {
      setColumns([
        {
          field: 'NUMBER',
          headerName: '번호',
          pinned: 'left',
          width: 100,
          valueFormatter: ({ value }) => value,
        },
        {
          field: 'NAME',
          headerName: '성명',
          pinned: 'left',
          width: 100,
        },
        {
          field: 'INSURANCE',
          headerName: '공제유형',
          width: 100,
        },
        {
          headerName: '급여내역',
          children: [
            {
              field: 'PAY',
              headerName: '시급',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'WORKDAY',
              headerName: '근무일수',
            },
            {
              field: 'WORKTIME',
              headerName: '총근무시간',
            },
            {
              field: 'MINUSWORKTIME',
              headerName: '지각,조퇴',
            },
            {
              field: 'REALWORKTIME',
              headerName: '실제근무시간',
            },
            {
              field: 'TOTALPAY',
              headerName: '임금액',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'WEEKPAYCOUNT',
              headerName: '주휴수당횟수',
              cellStyle: {
                textAlign: 'center',
              },
              cellRenderer: ({ data }: { data: IPay }) => {
                if (data.WEEKPAYCOUNT && data.WEEKPAYINFO && data.WEEKPAYINFO.length > 0) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleModal(data.WEEKPAYINFO!)}>
                      {data.WEEKPAYCOUNT} <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{data.WEEKPAYCOUNT}</div>;
              },
            },
            {
              field: 'WEEKPAY',
              headerName: '주휴수당',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'NIGHTPLUSPAY',
              headerName: '야간수당',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'PLUSPAY',
              headerName: '초과수당',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'RESTPAY',
              headerName: '휴일수당',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'MINUSPAY',
              headerName: '지각/조퇴차감',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'ALLPAY',
              headerName: '총임금액',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
          ],
        },
        {
          headerName: '공제내역',
          children: [
            {
              field: 'PENSION',
              headerName: '국민연금',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'HEALTH',
              headerName: '건강보험',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'LONGCARE',
              headerName: '장기요양',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'EMPLOYMENT',
              headerName: '고용보험',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'INCOME',
              headerName: '소득세',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'LOCALINCOME',
              headerName: '지방소득세',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
            {
              field: 'DEDUCTION',
              headerName: '공제합계',
              valueFormatter: ({ value }) => addComma(value),
              cellStyle: {
                textAlign: 'right',
              },
            },
          ],
        },
        {
          field: 'REALPAY',
          headerName: '실지급액',
          valueFormatter: ({ value }) => addComma(value),
          cellStyle: {
            textAlign: 'right',
          },
        },
        {
          headerName: '비고',
          children: [
            {
              field: 'START_END',
              headerName: '입사일/퇴사일',
            },
          ],
        },
      ]);
    }
  };

  const defaultColDef = React.useMemo<ColDef>(() => {
    return {
      filter: true,
      filterParams: {
        button: ['reset'],
        closeOnApply: true,
      },
      cellStyle: {
        textAlign: 'center',
      },
    };
  }, []);

  React.useEffect(() => {
    fetchData();
    handleColumns(payType);
  }, [currentMonth, payType?.value]);

  const AgGridRef = React.useRef<AgGridReact>(null);
  const gridComponentRef = React.useRef<ExcelExport | null>(null);

  return (
    <>
      <WithSideBarAndNavBar>
        <div className="payroll-register">
          <strong className="payroll-register__head">급여대장</strong>

          <div className="payroll-select">
            <div className="payroll-select__salary">
              <MonthInput onChange={handleDateChange} typeText value={currentMonth} />
            </div>
            <div>
              <Select
                className="payroll-select__dropdown"
                components={{
                  IndicatorSeparator: () => null,
                  DropdownIndicator: () => (
                    <div style={{ paddingRight: '15px' }}>
                      <ArrowDownIcon />
                    </div>
                  ),
                  Control: ({ children, ...props }: any) => {
                    return (
                      <components.Control {...props}>
                        <div
                          style={{
                            paddingLeft: '10px',
                            fontFamily: 'Spoqa Han Sans Neo',
                            fontWeight: '500',
                            fontSize: '16px',
                            color: '#868686',
                          }}>
                          급여유형
                        </div>
                        {children}
                      </components.Control>
                    );
                  },
                }}
                isSearchable={false}
                placeholder="시급"
                styles={colourStyles}
                options={options}
                value={payType}
                onChange={handlePayrollInformation}
              />
            </div>
          </div>
          <div className="payroll-register__body">
            <div className="payroll-register__body__head">
              <div>
                <div className="payroll-register__body__settlement-date">
                  급여정산일 :{' '}
                  <span className="payroll-register__body__settlement-date-data">
                    매달 {calDay === 1 ? '말일' : `${calDay - 1}일`}
                  </span>
                </div>
                <div className="payroll-register__body__settlement-date">
                  급여산정기간 :{' '}
                  <span className="payroll-register__body__settlement-date-data">
                    {moment(calPeriodStart).format('YYYY년MM월DD일')} ~ {moment(calPeriodEnd).format('YYYY년MM월DD일')}
                  </span>
                </div>
              </div>
              <div className="selected-details__excel">
                <button className="selected-details__excel-button" onClick={excelDown}>
                  Excel 다운
                </button>
              </div>
            </div>

            <AgGrid
              AgGridRef={AgGridRef}
              rowData={payData}
              colDefs={columns}
              defaultColDef={defaultColDef}
              pinnedBottomRowData={payTotalData}
            />
            {/* <PayrollGrid
              gridComponentRef={gridComponentRef}
              payData={payData}
              paytypeState={payType?.value ?? ''}
              calendarState={currentMonth}
              updateWeekPayModalDatas={toggleModal}
            /> */}
          </div>
          <div className="note">
            <div className="note__head">아래 사항을 참고해 주세요!</div>
            <div className="note__text">{`- 시급직 기본급 계산식 ={시급※ 근무시간(시간) +{ 시급 760 ×근무시간(분) }`}</div>
            <div className="note__text">{`- 급여대장 엑셀 다운로드 시 보여지는 데이터 그대로 저장됩니다.(정렬, 급여내역, 공제내역, 데이터 개수, 검색, 필터 등)`}</div>
            <div className="note__text">{`- 원단위 미만 소수점 자리의 값은 올림 처리하여 값을 보여좁니다.`}</div>
          </div>
        </div>
      </WithSideBarAndNavBar>
      <HolidayAllowanceModal isVisible={holidayAllowanceModal} onClose={toggleModal} data={weekPayInfoModalDatas} />
    </>
  );
};
export default PayrollRegister;
